import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';

import { GetBuyItAgainProductsDocument, GetBuyItAgainProductsQuery } from 'src/gql/graphql';
import { parseProduct, Product } from 'src/utils/helpers/product';
import useCart from 'src/hooks/use-cart';
import useUser from 'src/hooks/use-user';
import useStores from 'shared/hooks/use-stores';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'shared/hooks/use-ui';
import { tracker } from 'src/utils/analytics';
import useViewportVisibility, { ViewportVisibility } from 'src/hooks/use-viewport-visibility';
import { getItemListName } from 'src/utils/analytics/trackers/internal-gtm-tracker/helpers/item-list-name';
import { ProductRecommendationSource } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

type BuyItAgainSubstituteItem = GetBuyItAgainProductsQuery['getBuyItAgainProducts']['items'][number];
export type OriginalProduct = NonNullable<BuyItAgainSubstituteItem['originalProduct']>;

export type BuyItAgainAvailableItem = {
  type: 'available';
  product: Product;
};

export type BuyItAgainOutOfStockItem = {
  type: 'out-of-stock';
  originalProduct: OriginalProduct;
  substituteCandidates: Product[];
  originalProductId: string;
};

export type BuyItAgainCarouselItem = BuyItAgainAvailableItem | BuyItAgainOutOfStockItem;

export type UseBuyItAgainCarouselReturn = {
  items: BuyItAgainCarouselItem[];
  loading: boolean;
  handleViewAllClick: (destinationUrl: string) => void;
  viewportVisibility: ViewportVisibility;
  trackerSource: string | null;
};

export function useBuyItAgainCarousel(): UseBuyItAgainCarouselReturn {
  const router = useRouter();
  const cart = useCart();
  const user = useUser();
  const { User } = useStores();
  const { dispensary } = useDispensary();
  const { variant: menuVariant } = useUI();
  const dispensaryId = dispensary?.id;
  const menuType = useObserver(() => cart.menuType);
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const isPublicSessionPhoneLogin = useObserver(() => User?.isPublicSessionPhoneNumberLogin ?? false);
  const impressionTriggered = useRef(false);
  const viewportVisibility = useViewportVisibility();
  const { hasBeenVisible } = viewportVisibility;

  const { data, loading } = useQuery(GetBuyItAgainProductsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      dispensaryId,
      menuType,
      menuVariant,
    },
    skip: !dispensaryId || !isLoggedIn || isPublicSessionPhoneLogin,
  });

  const trackerSource = router
    ? getItemListName({
        router,
        source: ProductRecommendationSource.native,
        section: 'Buy it again',
      })
    : null;

  const items = useMemo(
    () =>
      (data?.getBuyItAgainProducts.items ?? []).reduce<BuyItAgainCarouselItem[]>((acc, item) => {
        if (!item.substitute) {
          acc.push({ type: 'available', product: parseProduct(item.product) });
        } else if (item.originalProduct) {
          acc.push({
            type: 'out-of-stock',
            originalProduct: item.originalProduct,
            substituteCandidates: item.substituteCandidates.map((p) => parseProduct(p)),
            originalProductId: item.substitute.originalProductId,
          });
        }
        return acc;
      }, []),
    [data?.getBuyItAgainProducts.items]
  );

  const availableProducts = useMemo(
    () =>
      items.filter((item): item is BuyItAgainAvailableItem => item.type === 'available').map((item) => item.product),
    [items]
  );

  const shouldTriggerImpression = availableProducts.length > 0 && hasBeenVisible && !impressionTriggered.current;

  const handleImpression = useCallback(() => {
    if (!trackerSource) {
      return;
    }
    tracker.buyItAgainImpression({ products: availableProducts, trackerSource });
  }, [availableProducts, trackerSource]);

  useEffect(() => {
    if (shouldTriggerImpression) {
      handleImpression();
      impressionTriggered.current = true;
    }
  }, [handleImpression, shouldTriggerImpression]);

  const handleViewAllClick = useCallback(
    (destinationUrl: string): void => {
      if (!trackerSource) {
        return;
      }
      tracker.buyItAgainViewAllClicked({ destinationUrl, trackerSource });
    },
    [trackerSource]
  );

  return {
    items,
    loading,
    handleViewAllClick,
    viewportVisibility,
    trackerSource,
  };
}
